import React from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Check,
  Zap,
  FlaskConical,
  FileText,
  Plug,
  HeadphonesIcon,
} from "lucide-react";

interface FreePlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Feature {
  label: string;
  free: string | boolean;
  individual: string | boolean;
}

interface FeatureGroup {
  title: string;
  icon: React.ReactNode;
  features: Feature[];
}

const featureGroups: FeatureGroup[] = [
  {
    title: "Usage & AI Models",
    icon: <Zap className="w-4 h-4" />,
    features: [
      {
        label: "Volume Limits",
        free: "10 pages / day",
        individual: "100 pages / day",
      },
      {
        label: "AI Models",
        free: "Standard models",
        individual: "Latest most powerful models",
      },
    ],
  },
  {
    title: "Chemistry Data",
    icon: <FlaskConical className="w-4 h-4" />,
    features: [
      {
        label: "Data Fidelity & DR Molfiles",
        free: "DR molfiles for first 10 molecules/day, then fallback to basic SMILES",
        individual: "Unlimited DR molfiles with full Markush & R-group support",
      },
      {
        label: "Combinatorial Enumeration",
        free: 'Teaser: "Markush structure represents X molecules"',
        individual: "Full combinatorial expansion of Markush",
      },
      {
        label: "Molecule Reporting",
        free: "Basic molecule list (Image + SMILES)",
        individual:
          "Full molecule inventory with text co-references & page mapping",
      },
      {
        label: "Reaction Scheme Mapping",
        free: "Flat list of reactants, conditions, and products",
        individual:
          "Fully mapped graphical reaction schemes showing step-by-step pathways",
      },
    ],
  },
  {
    title: "Reporting & Export",
    icon: <FileText className="w-4 h-4" />,
    features: [
      {
        label: "Export Formats",
        free: "Basic JSON, CSV",
        individual: "JSON, CSV, ORD (coming soon)",
      },
    ],
  },
  {
    title: "Integrations & API",
    icon: <Plug className="w-4 h-4" />,
    features: [
      {
        label: "Ecosystem Integrations",
        free: "Manual download & upload required",
        individual: "1-Click Push integrations (coming soon)",
      },
    ],
  },
  {
    title: "Support",
    icon: <HeadphonesIcon className="w-4 h-4" />,
    features: [
      {
        label: "Support",
        free: "Basic / Community support",
        individual: "Standard support",
      },
    ],
  },
];

const FreePlanUpgradeModal: React.FC<FreePlanUpgradeModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate("/upgrade");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Compare Plans
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              See what you unlock with the Individual plan
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Plan headers */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-0 px-6 pt-5 pb-4 flex-shrink-0">
          <div /> {/* spacer */}
          {/* Free card */}
          <div className="px-4 py-4 rounded-xl border border-gray-200 bg-gray-50 mr-3">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Free
            </p>
            <p className="text-2xl font-bold text-gray-900">
              £0{" "}
              <span className="text-sm font-normal text-gray-400">/ month</span>
            </p>
          </div>
          {/* Individual card */}
          <div className="px-4 py-4 rounded-xl border-2 border-[#1d365d] bg-[#1d365d]/5">
            <p className="text-sm font-semibold text-[#1d365d] uppercase tracking-wide mb-1">
              Individual
            </p>
            <p className="text-2xl font-bold text-gray-900">
              £25{" "}
              <span className="text-sm font-normal text-gray-400">/ month</span>
            </p>
          </div>
        </div>

        {/* Feature rows */}
        <div className="overflow-y-auto flex-1 px-6 pb-4">
          {featureGroups.map((group, gi) => (
            <div key={gi} className="mb-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 mt-2">
                <span className="text-[#1d365d]">{group.icon}</span>
                {group.title}
              </div>
              {group.features.map((feature, fi) => (
                <div
                  key={fi}
                  className="grid grid-cols-[1fr_1fr_1fr] gap-0 py-2.5 border-t border-gray-100 items-start"
                >
                  <p className="text-xs text-gray-500 pr-4 pt-0.5">
                    {feature.label}
                  </p>
                  {/* Free value */}
                  <div className="mr-3 pr-2">
                    {feature.free === false ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : feature.free === true ? (
                      <Check size={14} className="text-gray-400" />
                    ) : (
                      <p className="text-xs text-gray-600">{feature.free}</p>
                    )}
                  </div>
                  {/* Individual value */}
                  <div>
                    {feature.individual === false ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : feature.individual === true ? (
                      <Check size={14} className="text-[#1d365d]" />
                    ) : (
                      <p className="text-xs font-medium text-[#1d365d]">
                        {feature.individual}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Maybe later
          </button>
          <button
            onClick={handleUpgrade}
            className="px-5 py-2 text-sm font-medium bg-[#1d365d] hover:bg-[#16294a] text-white rounded-lg transition-colors"
          >
            Upgrade to Individual
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreePlanUpgradeModal;
